import React, { Component, ReactNode, useMemo, useState } from 'react'
import styles from './Base64Image.module.less'

class ImageErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
    constructor(props: { children: ReactNode }) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(): { hasError: boolean } {
        return { hasError: true }
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Unable to load base64 image:', error, errorInfo)
    }

    render() {
        if (this.state.hasError) {
            return <div className={styles.imageContainer}>Error loading base64 image</div>
        }

        return this.props.children
    }
}

const Base64ImageProcessor = ({ base64 }: { base64: string }) => {
    const [imageError, setImageError] = useState(false)
    const detectImageFormat = (base64Data: string): string | null => {
        if (!base64Data || base64Data.length < 20) {
            throw new Error('Base64 data too short for format detection')
        }

        const binaryString = atob(base64Data.substring(0, 20))
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
        }

        // Check common image signatures
        if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
            return 'jpeg'
        } else if (
            bytes[0] === 0x89 &&
            bytes[1] === 0x50 &&
            bytes[2] === 0x4e &&
            bytes[3] === 0x47
        ) {
            return 'png'
        } else if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
            return 'gif'
        } else if (
            bytes[0] === 0x52 &&
            bytes[1] === 0x49 &&
            bytes[2] === 0x46 &&
            bytes[3] === 0x46
        ) {
            return 'webp'
        }

        // Default to PNG if signature not recognized
        return 'png'
    }

    const dataUrl = useMemo(() => {
        if (!base64 || typeof base64 !== 'string') {
            throw new Error('Invalid base64 input')
        }

        // If the value already has data URL prefix, use it as is
        if (base64.startsWith('data:image/')) {
            return base64
        }

        // If it's a raw base64 string, detect format and add data URL prefix
        let imageFormat = 'png' // default format
        let base64Data = base64

        // Remove any existing data URL prefix if present
        if (base64.includes(',')) {
            const parts = base64.split(',')
            if (parts.length < 2) {
                throw new Error('Invalid base64 format')
            }
            base64Data = parts[1]
        }

        // Try to detect image format from base64 header
        const formatDetectionResult = detectImageFormat(base64Data)
        if (formatDetectionResult) {
            imageFormat = formatDetectionResult
        }

        return `data:image/${imageFormat};base64,${base64Data}`
    }, [base64])

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        console.error('Failed to load base64 image:', e)
        setImageError(true)
    }

    const handleImageLoad = () => {
        console.log('Base64 image loaded successfully')
    }

    // Throw error during render phase so error boundary can catch it
    if (imageError) {
        throw new Error('Unable to load base64 image')
    }

    return (
        <div className={styles.imageContainer}>
            <img
                className={styles.image}
                src={dataUrl}
                alt="Base64 Image"
                onError={handleImageError}
                onLoad={handleImageLoad}
            />
        </div>
    )
}

// Main component using error boundaries
export const Base64Image = ({ base64 }: { base64: string }) => {
    return (
        <ImageErrorBoundary>
            <Base64ImageProcessor base64={base64} />
        </ImageErrorBoundary>
    )
}
